/**
 * React hooks for the attention orchestrator. Thin veneer over
 * `attention.ts`'s subscribe API — the orchestrator itself has no
 * React dependency so it can be called from imperative code paths.
 */

import { useEffect, useState } from 'react';
import {
  type AttentionId,
  getCurrentAttention,
  subscribeAttention,
} from './attention';

/**
 * Returns the id of the currently-active attention item, or null
 * when nothing is holding the screen. Re-renders on every transition.
 */
export function useCurrentAttention(): AttentionId | null {
  const [current, setCurrent] = useState<AttentionId | null>(
    getCurrentAttention,
  );
  useEffect(() => {
    // subscribeAttention notifies us AFTER the orchestrator has
    // updated its internal state, so the value we receive is the
    // authoritative current.
    return subscribeAttention(setCurrent);
  }, []);
  return current;
}

/**
 * True iff any attention item is currently running. Convenience
 * wrapper around `useCurrentAttention() !== null`.
 */
export function useIsAttentionActive(): boolean {
  return useCurrentAttention() !== null;
}

/**
 * True iff the specific attention id is currently active. Convenience
 * wrapper that doesn't re-render on transitions of other ids — useful
 * for components like the tour spotlight that should mount only when
 * THEIR id is current.
 */
export function useIsAttentionId(id: AttentionId): boolean {
  return useCurrentAttention() === id;
}
